import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../api";
import { PageHeader, Card, Btn, Input, Field, Spinner, Empty, RequireTenant } from "../ui";
import { AjutorButton } from "../AjutorButton";
import { useAuth, usePermissions } from "../AuthContext";
import { FileText, Plus, Pencil, Trash2, X, Eye } from "lucide-react";
import { toast } from "sonner";
import RichTextEditor from "../RichTextEditor";

const empty = { name: "", subject: "", html_body: "", text_body: "" };

function PreviewModal({ template, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="template-preview-modal">
      <Card className="w-full max-w-2xl p-0 max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-semibold">{template.name}</h3>
            <p className="text-sm text-slate-500 mt-0.5">{template.subject}</p>
          </div>
          <button onClick={onClose} data-testid="close-preview-button"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="overflow-auto p-6 bg-slate-50">
          <div className="bg-white rounded-lg border border-slate-200 p-6 text-sm text-slate-800"
            data-testid="template-preview-body"
            dangerouslySetInnerHTML={{ __html: template.html_body || "<p class='text-slate-400'>Fara continut</p>" }} />
        </div>
      </Card>
    </div>
  );
}

function TemplateModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || empty);
  const [saving, setSaving] = useState(false);
  const editing = !!initial?.id;
  const save = async (e) => {
    e.preventDefault();
    const plain = (form.html_body || "").replace(/<[^>]*>/g, "").trim();
    if (!plain) { toast.error("Continutul emailului este obligatoriu"); return; }
    setSaving(true);
    try {
      if (editing) await api.patch(`/templates/${initial.id}`, form);
      else await api.post("/templates", form);
      toast.success(editing ? "Sablon actualizat" : "Sablon creat");
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <Card className="w-full max-w-2xl p-6 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{editing ? "Editeaza sablon" : "Sablon nou"}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <form onSubmit={save} data-testid="template-form">
          <Field label="Nume *"><Input data-testid="template-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Subiect *"><Input data-testid="template-subject-input" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></Field>
          <Field label="Continut HTML *">
            <RichTextEditor value={form.html_body} onChange={(html) => setForm({ ...form, html_body: html })} />
          </Field>
          <div className="flex justify-end gap-2">
            <Btn variant="outline" type="button" onClick={onClose}>Anuleaza</Btn>
            <Btn type="submit" disabled={saving} data-testid="save-template-button">Salveaza</Btn>
          </div>
        </form>
      </Card>
    </div>
  );
}

function TemplatesInner() {
  const { activeTenant } = useAuth();
  const { can } = usePermissions();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | {} | template
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/templates"); setItems(data.items); }
    finally { setLoading(false); }
  }, [activeTenant]);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Stergi acest sablon?")) return;
    try { await api.delete(`/templates/${id}`); toast.success("Sters"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        {can("sabloane", "add") && <Btn onClick={() => setModal({})} data-testid="add-template-button"><Plus className="w-4 h-4" /> Sablon nou</Btn>}
      </div>
      {loading ? <Spinner /> : items.length === 0 ? (
        <Card><Empty icon={FileText} title="Niciun sablon" hint="Creeaza primul tau sablon de email." /></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="templates-grid">
          {items.map((t) => (
            <Card key={t.id} className="p-5 flex flex-col" data-testid={`template-card-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center"><FileText className="w-[18px] h-[18px]" /></div>
                <div className="flex gap-1">
                  <button onClick={() => setPreview(t)} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-emerald-600" data-testid={`preview-template-${t.id}`}><Eye className="w-4 h-4" /></button>
                  {can("sabloane", "edit") && <button onClick={() => setModal(t)} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-700" data-testid={`edit-template-${t.id}`}><Pencil className="w-4 h-4" /></button>}
                  {can("sabloane", "delete") && <button onClick={() => del(t.id)} className="p-1.5 rounded-md hover:bg-red-50 text-slate-400 hover:text-red-600" data-testid={`delete-template-${t.id}`}><Trash2 className="w-4 h-4" /></button>}
                </div>
              </div>
              <h3 className="mt-3 font-semibold text-slate-900">{t.name}</h3>
              <p className="text-sm text-slate-500 mt-0.5 line-clamp-1">{t.subject}</p>
            </Card>
          ))}
        </div>
      )}
      {modal && <TemplateModal initial={modal.id ? modal : null} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {preview && <PreviewModal template={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

export default function Templates() {
  return (
    <div>
      <PageHeader title="Sabloane" subtitle="Sabloanele de email reutilizabile in campanii." />
      <AjutorButton file="ajutor_sabloane.pdf" />
      <RequireTenant><TemplatesInner /></RequireTenant>
    </div>
  );
}
